import React, { useState, useEffect, useReducer, lazy, Suspense } from 'react'

import { getMessageList, find_sos, find_head_of_class, find_counsellor, get_chat_config } from '../../server/server'
import { useUser } from '../../context/user'
import '../../styles/Chat.css'
import { ReactComponent as MagnifierIcon } from '../../svg/normalSvgs/MagnifierIcon.svg'
import { useSocket } from '../../context/socket'
import useWindowFocus from 'use-window-focus'
import { useTranslation } from 'react-i18next'
import i18n from '../../i18n'
import { useToasts } from 'react-toast-notifications'
import LoadingSpinner from '../homescreen/LoadingSpinner'
import ChatTypeSection from './ChatTypeSection'
import ChatHeaderSection from './ChatHeaderSection'
import ChatListItem from './ChatListItem'
//import ChatSection from './ChatSection'
import ChatSectionSkeleton from './skeleton/ChatSectionSkeleton'
import ConversationSkeleton from './skeleton/ConversationSkeleton'

const ChatSection = lazy(() => import('./ChatSection'))

const Conversation = lazy(() => import('../Conversation'))

export default function ChatScreen() {
    const { user } = useUser()
    const { t } = useTranslation()
    const { addToast } = useToasts()
    const { socketStates, changeSocketState } = useSocket()
    let timeout = null
    const windowFocused = useWindowFocus()
    const [state, setState] = useReducer((oldState, newState) => ({ ...oldState, ...newState }), {
        chats: [],
        chatSize: -1,
        refreshing: false,
        initLoading: true,
        loadingNewData: false,
        searchText: '',
        selectedChat: null,
        chatDisabled: false,
        anonymousChatEnabled: false,
    })
    const { chats, chatSize, refreshing, initLoading, loadingNewData, searchText, selectedChat, chatDisabled, anonymousChatEnabled } = state

    //const [selectedConversation, setSelectedConversation] = useState('')
    const [visibleChats, setVisibleChats] = useState(true)

    const refreshConversationList = async () => await getConversationList(0)

    useEffect(() => {
        if (windowFocused) refreshConversationList()
    }, [socketStates.lastMessage, windowFocused, visibleChats])

    useEffect(() => {
        if (windowFocused && selectedChat) selectChat(selectedChat)
    }, [windowFocused])

    useEffect(() => {
        getChatConfig()

        return () => {
            changeSocketState({ openedConversation: null })
        }
    }, [])

    const getChatConfig = async () => {
        const resp = await get_chat_config()
        if (resp.status === 200) {
            setState({ chatDisabled: resp.data?.chatDisabled, anonymousChatEnabled: resp.data.anonymousChatEnabled })
        }
    }

    const loadCounselor = async () => {
        const resp = await find_counsellor(user)
        if (resp.status === 200) {
            const counsellor = resp.data
            return counsellor
        }
        return null
    }

    const doSearch = (searchText) => {
        if (timeout) clearTimeout(timeout)
        setState({ searchText })
        timeout = setTimeout(() => {
            getConversationList(0, searchText)
        }, 400)
    }

    const loadSos = async () => {
        const resp = await find_sos(user)
        if (resp.status === 200) {
            const sos = resp.data
            return sos
        }
        return null
    }

    const loadTeacher = async () => {
        const resp = await find_head_of_class(user)
        if (resp.status === 200) {
            const teacher = resp.data
            return teacher
        } else {
            return null
        }
    }

    const getConversationList = async (number = chats.length, search = searchText) => {
        const data = { number_of_loaded: number, search_text: search, anonymousChat: !visibleChats }
        const resp = await getMessageList({ data })
        if (resp.status === 200) {
            const data = resp.data
            const conversations = data.list
            const size = data.size
            const oldList = number !== 0 ? chats : []
            const newList = [...oldList, ...conversations]

            setState({
                chats: newList,
                chatSize: size,
                initLoading: false,
                loadingNewData: false,
            })
        } else {
            setState({
                initLoading: false,
            })
        }
    }

    const selectChat = (chat) => {
        let _chat = { ...chat }
        delete _chat.last_message
        checkUnseenConversations(chat.conversation_id)
        return _chat
    }

    const selectChatExternally = (conversation) => {
        if (selectedChat?.conversation_id === conversation?.conversation_id) return
        setState({ selectedChat: selectChat(conversation) })
    }

    const checkUnseenConversations = (conversation_id) => {
        const index = socketStates?.unseenConversations?.findIndex((e) => e === conversation_id)
        let _socket = { ...socketStates }
        _socket['openedConversation'] = conversation_id
        if (index > -1) {
            const ids = socketStates?.unseenConversations?.filter((e) => e !== conversation_id)
            _socket['unseenConversations'] = ids
        }
        changeSocketState(_socket)
    }

    const openNewConversation = async (getRecipients, type) => {
        const recipients = await getRecipients()
        // An empty list means the school has nobody configured for this chat type
        const hasRecipients = Array.isArray(recipients) ? recipients.length > 0 : !!recipients
        if (hasRecipients) {
            let conversation = {
                members: [user.id, ...recipients],
                new: true,
                type,
            }
            if (!visibleChats) conversation['anonymous_chat'] = true
            setState({ selectedChat: conversation })
            //setSelectedConversation(type)
        } else {
            const unavailableMessages = { teacher: 'noTeacherAvailable', counselor: 'noCounselorAvailable', sos: 'noSosAvailable' }
            addToast(t(unavailableMessages[type]), { appearance: 'warning', autoDismiss: true })
        }
    }
    const handleVisibleChats = (state) => {
        setVisibleChats(state)
        setState({ selectedChat: null })
    }

    return (
        <div className={`chat-container ${i18n.language === 'ar' ? 'ar-trans' : ''}`} style={{ backgroundColor: visibleChats ? '#ECEBF8' : 'var(--deep-ocean)' }}>
            <div className="chat-main">
                <Suspense fallback={<ChatSectionSkeleton />}>
                    <ChatSection
                        visibleChats={visibleChats}
                        chatDisabled={chatDisabled}
                        anonymousChatEnabled={anonymousChatEnabled}
                        searchText={searchText}
                        doSearch={doSearch}
                        openNewConversation={openNewConversation}
                        loadCounselor={loadCounselor}
                        loadSos={loadSos}
                        loadTeacher={loadTeacher}
                        chats={chats}
                        initLoading={initLoading}
                        selectedChat={selectedChat}
                        selectChatExternally={selectChatExternally}
                        handleVisibleChats={handleVisibleChats}
                    />
                </Suspense>
                <div className="chat-conversation-section">
                    {selectedChat && (
                        <Suspense fallback={<ConversationSkeleton visibleChats={visibleChats} />}>
                            <Conversation
                                conversation={selectedChat}
                                refreshConversationList={refreshConversationList}
                                selectChatExternally={selectChatExternally}
                                visibleChats={visibleChats}
                            />
                        </Suspense>
                    )}
                </div>
            </div>
        </div>
    )
}
